import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import { Html } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import {
  BufferGeometry,
  Float32BufferAttribute,
  Mesh,
  Vector3,
  DoubleSide,
  TubeGeometry,
  CatmullRomCurve3,
  ShaderMaterial,
  Color,
  RingGeometry,
  Quaternion,
} from "three";
import type { CenterlineData } from "./useTunnelCenterline";
import type { ProfileAssignment } from "./tunnelProfileShapes";
import { getProfileAtStation } from "./tunnelProfileShapes";
import type { Drawing3DLine, Drawing3DPolygon, TunnelPoint3D, DrawToolType } from "./drawings3D";
import {
  DRAW_TOOL_CONFIG,
  loadDrawings3D,
  saveDrawings3D,
  loadPolygons3D,
  savePolygons3D,
  tunnelPoint3DToWorld,
  worldToTunnelPoint3D,
} from "./drawings3D";
import { useProject } from "./ProjectContext";

interface TunnelDraw3DProps {
  data: CenterlineData;
  profileAssignments: ProfileAssignment[];
  drawMode: boolean;
  drawToolType?: DrawToolType;
  brushScale: number;
  onBrushScaleChange: (scale: number) => void;
}

const FRAME_SAMPLES = 2000;
const RING_SPACING = 2; // denser mesh for smoother raycasting

/**
 * Builds a triangulated tunnel surface mesh from profile rings.
 * Returns a BufferGeometry suitable for raycasting.
 */
function buildTunnelSurfaceMesh(
  data: CenterlineData,
  profileAssignments: ProfileAssignment[],
  frames: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints: Vector3[],
): BufferGeometry {
  const totalLength = data.totalLength;
  const ringCount = Math.floor(totalLength / RING_SPACING) + 1;

  // Build rings of 3D vertices
  const rings: Vector3[][] = [];
  for (let i = 0; i < ringCount; i++) {
    const station = i * RING_SPACING;
    const t = Math.min(station / totalLength, 1);
    const frameIdx = Math.min(Math.round(t * FRAME_SAMPLES), FRAME_SAMPLES);

    const center = curvePoints[frameIdx];
    const normal = frames.normals[frameIdx];
    const binormal = frames.binormals[frameIdx];

    const profile = getProfileAtStation(station, profileAssignments);
    const ring = profile.points.map(
      (p2d) =>
        new Vector3(
          center.x + p2d.x * binormal.x - p2d.y * normal.x,
          center.y + p2d.x * binormal.y - p2d.y * normal.y,
          center.z + p2d.x * binormal.z - p2d.y * normal.z,
        ),
    );
    rings.push(ring);
  }

  // Triangulate between adjacent rings (triangle strip)
  const positions: number[] = [];
  for (let i = 0; i < rings.length - 1; i++) {
    const rA = rings[i];
    const rB = rings[i + 1];
    const count = Math.min(rA.length, rB.length);
    for (let j = 0; j < count - 1; j++) {
      // Two triangles per quad
      const a = rA[j], b = rA[j + 1], c = rB[j], d = rB[j + 1];
      positions.push(a.x, a.y, a.z, c.x, c.y, c.z, b.x, b.y, b.z);
      positions.push(b.x, b.y, b.z, c.x, c.y, c.z, d.x, d.y, d.z);
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

/**
 * Triangulate a polygon and project onto tunnel surface.
 * Uses a grid rasterization approach in (station, perimeter) space to avoid
 * fold/self-intersection artifacts from fan triangulation on L-shaped or
 * corner-wrapping polygons.
 */
function buildPolygonGeometry(
  vertices: TunnelPoint3D[],
  data: CenterlineData,
  frames: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints: Vector3[],
  profileAssignments: ProfileAssignment[],
): BufferGeometry {
  if (vertices.length < 3) return new BufferGeometry();

  // Get profile for perimeter-based interpolation
  const avgStation = vertices.reduce((s, v) => s + v.station, 0) / vertices.length;
  const profile = getProfileAtStation(avgStation, profileAssignments);
  const profilePts = profile.points;

  // Build cumulative perimeter distance
  const perimDist: number[] = [0];
  for (let i = 1; i < profilePts.length; i++) {
    const dx = profilePts[i].x - profilePts[i - 1].x;
    const dy = profilePts[i].y - profilePts[i - 1].y;
    perimDist.push(perimDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }
  const totalPerim = perimDist[perimDist.length - 1];

  function uvToPerimeter(u: number, v: number): number {
    let bestDist = Infinity;
    let bestPerim = 0;
    for (let i = 0; i < profilePts.length - 1; i++) {
      const ax = profilePts[i].x, ay = profilePts[i].y;
      const bx = profilePts[i + 1].x, by = profilePts[i + 1].y;
      const abx = bx - ax, aby = by - ay;
      const apx = u - ax, apy = v - ay;
      const abLenSq = abx * abx + aby * aby;
      if (abLenSq < 1e-12) continue;
      const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
      const px = ax + abx * t, py = ay + aby * t;
      const d = (u - px) * (u - px) + (v - py) * (v - py);
      if (d < bestDist) {
        bestDist = d;
        bestPerim = perimDist[i] + (perimDist[i + 1] - perimDist[i]) * t;
      }
    }
    return bestPerim;
  }

  function perimeterToUV(p: number): { u: number; v: number } {
    p = Math.max(0, Math.min(totalPerim, p));
    for (let i = 0; i < perimDist.length - 1; i++) {
      if (p >= perimDist[i] && p <= perimDist[i + 1]) {
        const segLen = perimDist[i + 1] - perimDist[i];
        const t = segLen > 1e-12 ? (p - perimDist[i]) / segLen : 0;
        return {
          u: profilePts[i].x + (profilePts[i + 1].x - profilePts[i].x) * t,
          v: profilePts[i].y + (profilePts[i + 1].y - profilePts[i].y) * t,
        };
      }
    }
    const last = profilePts[profilePts.length - 1];
    return { u: last.x, v: last.y };
  }

  // Convert polygon vertices to 2D (station, perim) space
  const poly2D: { s: number; p: number }[] = vertices.map((v) => ({
    s: v.station,
    p: uvToPerimeter(v.u, v.v),
  }));

  // Bounding box
  let minS = Infinity, maxS = -Infinity;
  let minP = Infinity, maxP = -Infinity;
  for (const v of poly2D) {
    if (v.s < minS) minS = v.s;
    if (v.s > maxS) maxS = v.s;
    if (v.p < minP) minP = v.p;
    if (v.p > maxP) maxP = v.p;
  }

  // Grid resolution ~0.5m for reasonable triangle count
  const cellSize = 0.5;
  const cols = Math.max(2, Math.ceil((maxS - minS) / cellSize));
  const rows = Math.max(2, Math.ceil((maxP - minP) / cellSize));
  const stepS = (maxS - minS) / cols;
  const stepP = (maxP - minP) / rows;

  // Point-in-polygon test (ray casting) in 2D (station, perim) space
  function pointInPoly(s: number, p: number): boolean {
    let inside = false;
    for (let i = 0, j = poly2D.length - 1; i < poly2D.length; j = i++) {
      const si = poly2D[i].s, pi = poly2D[i].p;
      const sj = poly2D[j].s, pj = poly2D[j].p;
      if ((pi > p) !== (pj > p) && s < (sj - si) * (p - pi) / (pj - pi) + si) {
        inside = !inside;
      }
    }
    return inside;
  }

  // Helper: convert grid (station, perim) to world position via tunnel surface
  function gridToWorld(s: number, p: number): Vector3 {
    const uv = perimeterToUV(p);
    return tunnelPoint3DToWorld(
      { station: s, u: uv.u, v: uv.v },
      data, frames, curvePoints, FRAME_SAMPLES,
    );
  }

  // Rasterize: for each grid cell, if its center is inside the polygon,
  // emit two triangles (a quad) with vertices projected onto the tunnel surface.
  const positions: number[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cs = minS + (c + 0.5) * stepS;
      const cp = minP + (r + 0.5) * stepP;
      if (!pointInPoly(cs, cp)) continue;

      // Quad corners
      const s0 = minS + c * stepS;
      const s1 = minS + (c + 1) * stepS;
      const p0 = minP + r * stepP;
      const p1 = minP + (r + 1) * stepP;

      const v00 = gridToWorld(s0, p0);
      const v10 = gridToWorld(s1, p0);
      const v01 = gridToWorld(s0, p1);
      const v11 = gridToWorld(s1, p1);

      // Triangle 1: v00, v10, v01
      positions.push(v00.x, v00.y, v00.z);
      positions.push(v10.x, v10.y, v10.z);
      positions.push(v01.x, v01.y, v01.z);
      // Triangle 2: v10, v11, v01
      positions.push(v10.x, v10.y, v10.z);
      positions.push(v11.x, v11.y, v11.z);
      positions.push(v01.x, v01.y, v01.z);
    }
  }

  if (positions.length === 0) return new BufferGeometry();

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

/**
 * 2D polygon union in (s, p) space using grid rasterization.
 * Rasterizes both polygons onto a fine grid, ORs them, traces the boundary,
 * and simplifies with Douglas-Peucker to produce clean edges.
 *
 * Returns the union boundary, or null if polygons don't overlap (disjoint).
 */
function polygonUnion2D(
  polyA: { s: number; p: number }[],
  polyB: { s: number; p: number }[],
): { s: number; p: number }[] | null {
  if (polyA.length < 3 || polyB.length < 3) return null;

  type Pt = { s: number; p: number };

  // Check if polygons' bounding boxes overlap (with small margin)
  let aMinS = Infinity, aMaxS = -Infinity, aMinP = Infinity, aMaxP = -Infinity;
  for (const v of polyA) {
    if (v.s < aMinS) aMinS = v.s;
    if (v.s > aMaxS) aMaxS = v.s;
    if (v.p < aMinP) aMinP = v.p;
    if (v.p > aMaxP) aMaxP = v.p;
  }
  let bMinS = Infinity, bMaxS = -Infinity, bMinP = Infinity, bMaxP = -Infinity;
  for (const v of polyB) {
    if (v.s < bMinS) bMinS = v.s;
    if (v.s > bMaxS) bMaxS = v.s;
    if (v.p < bMinP) bMinP = v.p;
    if (v.p > bMaxP) bMaxP = v.p;
  }

  // No bounding box overlap → disjoint (with small margin for touching edges)
  const margin = 0.05;
  if (aMaxS < bMinS - margin || bMaxS < aMinS - margin ||
      aMaxP < bMinP - margin || bMaxP < aMinP - margin) {
    return null;
  }

  // Combined bounding box with padding
  const pad = 0.1;
  const minS = Math.min(aMinS, bMinS) - pad;
  const maxS = Math.max(aMaxS, bMaxS) + pad;
  const minP = Math.min(aMinP, bMinP) - pad;
  const maxP = Math.max(aMaxP, bMaxP) + pad;

  // Fine grid — use ~0.05m cells for accurate boundary
  const cellSize = 0.05;
  const cols = Math.max(4, Math.ceil((maxS - minS) / cellSize));
  const rows = Math.max(4, Math.ceil((maxP - minP) / cellSize));
  const stepS = (maxS - minS) / cols;
  const stepP = (maxP - minP) / rows;
  const W = cols + 1;
  const H = rows + 1;

  // Limit grid size to avoid excessive memory/time
  if (W * H > 2_000_000) return null;

  const grid = new Uint8Array(W * H);

  // Scanline rasterize a polygon onto the grid
  function rasterize(poly: Pt[]) {
    for (let r = 0; r < H; r++) {
      const py = minP + r * stepP;
      const crossings: number[] = [];
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const yi = poly[i].p, yj = poly[j].p;
        if ((yi <= py && yj > py) || (yj <= py && yi > py)) {
          const t = (py - yi) / (yj - yi);
          crossings.push(poly[i].s + t * (poly[j].s - poly[i].s));
        }
      }
      crossings.sort((a, b) => a - b);
      for (let k = 0; k + 1 < crossings.length; k += 2) {
        const cMin = Math.max(0, Math.floor((crossings[k] - minS) / stepS));
        const cMax = Math.min(W - 1, Math.ceil((crossings[k + 1] - minS) / stepS));
        for (let c = cMin; c <= cMax; c++) {
          grid[r * W + c] = 1;
        }
      }
    }
  }

  // Rasterize A, check if any B cell overlaps, then rasterize B
  rasterize(polyA);

  // Before rasterizing B, check overlap: scan B and see if any cell is already set
  let hasOverlap = false;
  outerCheck:
  for (let r = 0; r < H; r++) {
    const py = minP + r * stepP;
    const crossings: number[] = [];
    for (let i = 0, j = polyB.length - 1; i < polyB.length; j = i++) {
      const yi = polyB[i].p, yj = polyB[j].p;
      if ((yi <= py && yj > py) || (yj <= py && yi > py)) {
        const t = (py - yi) / (yj - yi);
        crossings.push(polyB[i].s + t * (polyB[j].s - polyB[i].s));
      }
    }
    crossings.sort((a, b) => a - b);
    for (let k = 0; k + 1 < crossings.length; k += 2) {
      const cMin = Math.max(0, Math.floor((crossings[k] - minS) / stepS));
      const cMax = Math.min(W - 1, Math.ceil((crossings[k + 1] - minS) / stepS));
      for (let c = cMin; c <= cMax; c++) {
        if (grid[r * W + c] === 1) {
          hasOverlap = true;
          break outerCheck;
        }
      }
    }
  }

  if (!hasOverlap) return null; // disjoint

  rasterize(polyB);

  // Boundary tracing (Moore neighborhood)
  const contourS: number[] = [];
  const contourP: number[] = [];

  let startC = -1, startR = -1;
  outer:
  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      if (grid[r * W + c] === 1) {
        const hasEmpty =
          c === 0 || r === 0 || c === W - 1 || r === H - 1 ||
          grid[r * W + (c - 1)] === 0 ||
          grid[r * W + (c + 1)] === 0 ||
          grid[(r - 1) * W + c] === 0 ||
          grid[(r + 1) * W + c] === 0;
        if (hasEmpty) {
          startC = c;
          startR = r;
          break outer;
        }
      }
    }
  }

  if (startC < 0) return null;

  const dirs = [
    [1, 0], [1, 1], [0, 1], [-1, 1],
    [-1, 0], [-1, -1], [0, -1], [1, -1],
  ];

  const visited = new Set<number>();
  let cc = startC, cr = startR;
  let dir = 0;
  const maxSteps = W * H * 2;

  for (let step = 0; step < maxSteps; step++) {
    const key = cr * W + cc;
    if (!visited.has(key)) {
      visited.add(key);
      contourS.push(minS + cc * stepS);
      contourP.push(minP + cr * stepP);
    }

    let found = false;
    const startDir = (dir + 5) % 8;
    for (let i = 0; i < 8; i++) {
      const d = (startDir + i) % 8;
      const nc = cc + dirs[d][0];
      const nr = cr + dirs[d][1];
      if (nc >= 0 && nc < W && nr >= 0 && nr < H && grid[nr * W + nc] === 1) {
        const isBoundary =
          nc === 0 || nr === 0 || nc === W - 1 || nr === H - 1 ||
          grid[nr * W + (nc - 1)] === 0 ||
          grid[nr * W + (nc + 1)] === 0 ||
          grid[(nr - 1) * W + nc] === 0 ||
          grid[(nr + 1) * W + nc] === 0;
        if (isBoundary) {
          dir = d;
          cc = nc;
          cr = nr;
          found = true;
          break;
        }
      }
    }

    if (!found) break;
    if (cc === startC && cr === startR && step > 0) break;
  }

  if (contourS.length < 3) return null;

  // Douglas-Peucker simplification to reduce vertex count and smooth edges
  function dpSimplify(pts: Pt[], epsilon: number): Pt[] {
    if (pts.length <= 3) return pts;

    function dpRecurse(start: number, end: number, keep: boolean[]) {
      let maxDist = 0;
      let maxIdx = start;
      const a = pts[start], b = pts[end];
      const dx = b.s - a.s, dy = b.p - a.p;
      const lenSq = dx * dx + dy * dy;

      for (let i = start + 1; i < end; i++) {
        let dist: number;
        if (lenSq < 1e-12) {
          dist = Math.sqrt((pts[i].s - a.s) ** 2 + (pts[i].p - a.p) ** 2);
        } else {
          const t = ((pts[i].s - a.s) * dx + (pts[i].p - a.p) * dy) / lenSq;
          const ps = a.s + t * dx, pp = a.p + t * dy;
          dist = Math.sqrt((pts[i].s - ps) ** 2 + (pts[i].p - pp) ** 2);
        }
        if (dist > maxDist) {
          maxDist = dist;
          maxIdx = i;
        }
      }

      if (maxDist > epsilon) {
        keep[maxIdx] = true;
        if (maxIdx - start > 1) dpRecurse(start, maxIdx, keep);
        if (end - maxIdx > 1) dpRecurse(maxIdx, end, keep);
      }
    }

    const keep = new Array(pts.length).fill(false);
    keep[0] = true;
    keep[pts.length - 1] = true;
    dpRecurse(0, pts.length - 1, keep);
    return pts.filter((_, i) => keep[i]);
  }

  let result: Pt[] = contourS.map((s, i) => ({ s, p: contourP[i] }));

  // Simplify: use epsilon proportional to cell size for clean edges
  result = dpSimplify(result, stepS * 1.5);

  // Cap vertex count
  if (result.length > 200) {
    const skip = Math.max(1, Math.floor(result.length / 200));
    result = result.filter((_, i) => i % skip === 0);
  }

  return result.length >= 3 ? result : null;
}


/**
 * Given freehand paint stroke points in tunnel-local coords, rasterize them
 * with a brush radius onto a 2D grid, then extract the outer contour.
 *
 * Rasterizes in (station, perimeterDistance) space using the actual tunnel
 * profile shape, so painting across floor, wall, and arch works correctly.
 *
 * profileAssignments is needed to look up the profile at each station.
 */
function extractBoundaryFromPaintStroke(
  points: TunnelPoint3D[],
  brushRadius: number,
  profileAssignments: ProfileAssignment[],
  existingPolygons?: Drawing3DPolygon[],
): { boundary: TunnelPoint3D[]; mergedIndices: number[] } {
  if (points.length < 2) return { boundary: points, mergedIndices: [] };

  // Get a representative profile (use the average station of the stroke)
  const avgStation = points.reduce((s, p) => s + p.station, 0) / points.length;

  // Helper: build perimeter lookup for a given profile
  function buildPerimLookup(prof: { points: import("three").Vector2[] }) {
    const pts = prof.points;
    const pd: number[] = [0];
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i].x - pts[i - 1].x;
      const dy = pts[i].y - pts[i - 1].y;
      pd.push(pd[i - 1] + Math.sqrt(dx * dx + dy * dy));
    }
    const tp = pd[pd.length - 1];

    function uvToP(u: number, v: number): number {
      let bestDist = Infinity;
      let bestPerim = 0;
      for (let i = 0; i < pts.length - 1; i++) {
        const ax = pts[i].x, ay = pts[i].y;
        const bx = pts[i + 1].x, by = pts[i + 1].y;
        const abx = bx - ax, aby = by - ay;
        const apx = u - ax, apy = v - ay;
        const abLenSq = abx * abx + aby * aby;
        if (abLenSq < 1e-12) continue;
        const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
        const px = ax + abx * t, py = ay + aby * t;
        const d = (u - px) * (u - px) + (v - py) * (v - py);
        if (d < bestDist) {
          bestDist = d;
          bestPerim = pd[i] + (pd[i + 1] - pd[i]) * t;
        }
      }
      return bestPerim;
    }

    function pToUV(p: number): { u: number; v: number } {
      p = Math.max(0, Math.min(tp, p));
      for (let i = 0; i < pd.length - 1; i++) {
        if (p >= pd[i] && p <= pd[i + 1]) {
          const segLen = pd[i + 1] - pd[i];
          const t = segLen > 1e-12 ? (p - pd[i]) / segLen : 0;
          return {
            u: pts[i].x + (pts[i + 1].x - pts[i].x) * t,
            v: pts[i].y + (pts[i + 1].y - pts[i].y) * t,
          };
        }
      }
      const last = pts[pts.length - 1];
      return { u: last.x, v: last.y };
    }

    return { uvToP, pToUV, totalPerim: tp };
  }

  // Cache profile lookups by profile identity
  const profileCache = new Map<object, ReturnType<typeof buildPerimLookup>>();
  function getLookup(station: number) {
    const prof = getProfileAtStation(station, profileAssignments);
    let lookup = profileCache.get(prof);
    if (!lookup) {
      lookup = buildPerimLookup(prof);
      profileCache.set(prof, lookup);
    }
    return lookup;
  }

  // Use the stroke's profile for stroke points
  const strokeLookup = getLookup(avgStation);
  const { uvToP: uvToPerimeter, totalPerim } = strokeLookup;

  // Convert all points to (station, perimeterDistance)
  const mapped = points.map((pt) => ({
    station: pt.station,
    perim: uvToPerimeter(pt.u, pt.v),
  }));

  // Convert existing polygon vertices using per-vertex station profile
  const existingMapped: { s: number; p: number }[][] = [];
  if (existingPolygons) {
    for (const poly of existingPolygons) {
      existingMapped.push(poly.vertices.map((v) => {
        const vLookup = getLookup(v.station);
        return { s: v.station, p: vLookup.uvToP(v.u, v.v) };
      }));
    }
  }

  // Bounding box in (station, perim) space — stroke only
  let minS = Infinity, maxS = -Infinity;
  let minP = Infinity, maxP = -Infinity;
  for (const m of mapped) {
    if (m.station < minS) minS = m.station;
    if (m.station > maxS) maxS = m.station;
    if (m.perim < minP) minP = m.perim;
    if (m.perim > maxP) maxP = m.perim;
  }

  const pad = brushRadius * 1.2;
  minS -= pad; maxS += pad;
  minP -= pad; maxP += pad;
  // Clamp perimeter to valid range
  minP = Math.max(0, minP);
  maxP = Math.min(totalPerim, maxP);

  // Grid — stroke only
  const cellSize = 0.15;
  const cols = Math.max(4, Math.ceil((maxS - minS) / cellSize));
  const rows = Math.max(4, Math.ceil((maxP - minP) / cellSize));
  const stepS = (maxS - minS) / cols;
  const stepP = (maxP - minP) / rows;

  const W = cols + 1;
  const H = rows + 1;
  const grid = new Uint8Array(W * H);
  const br2 = brushRadius * brushRadius;

  for (const m of mapped) {
    const ci = Math.round((m.station - minS) / stepS);
    const ri = Math.round((m.perim - minP) / stepP);
    const spread = Math.ceil(brushRadius / Math.min(stepS, stepP)) + 1;

    for (let di = -spread; di <= spread; di++) {
      for (let dj = -spread; dj <= spread; dj++) {
        const c = ci + di;
        const r = ri + dj;
        if (c < 0 || c >= W || r < 0 || r >= H) continue;
        const ds = (minS + c * stepS) - m.station;
        const dp = (minP + r * stepP) - m.perim;
        if (ds * ds + dp * dp <= br2) {
          grid[r * W + c] = 1;
        }
      }
    }
  }

  // Dilate by 1 cell
  const dilated = new Uint8Array(W * H);
  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      if (grid[r * W + c] === 1) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr, nc = c + dc;
            if (nr >= 0 && nr < H && nc >= 0 && nc < W) {
              dilated[nr * W + nc] = 1;
            }
          }
        }
      }
    }
  }

  // Boundary tracing (Moore neighborhood)
  const contourS: number[] = [];
  const contourP: number[] = [];

  let startC = -1, startR = -1;
  outer:
  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      if (dilated[r * W + c] === 1) {
        const hasEmpty =
          c === 0 || r === 0 || c === W - 1 || r === H - 1 ||
          dilated[r * W + (c - 1)] === 0 ||
          dilated[r * W + (c + 1)] === 0 ||
          dilated[(r - 1) * W + c] === 0 ||
          dilated[(r + 1) * W + c] === 0;
        if (hasEmpty) {
          startC = c;
          startR = r;
          break outer;
        }
      }
    }
  }

  if (startC < 0) return { boundary: points, mergedIndices: [] };

  const dirs = [
    [1, 0], [1, 1], [0, 1], [-1, 1],
    [-1, 0], [-1, -1], [0, -1], [1, -1],
  ];

  const visited = new Set<number>();
  let cc = startC, cr = startR;
  let dir = 0;
  const maxSteps = W * H * 2;

  for (let step = 0; step < maxSteps; step++) {
    const key = cr * W + cc;
    if (!visited.has(key)) {
      visited.add(key);
      contourS.push(minS + cc * stepS);
      contourP.push(minP + cr * stepP);
    }

    let found = false;
    const startDir = (dir + 5) % 8;
    for (let i = 0; i < 8; i++) {
      const d = (startDir + i) % 8;
      const nc = cc + dirs[d][0];
      const nr = cr + dirs[d][1];
      if (nc >= 0 && nc < W && nr >= 0 && nr < H && dilated[nr * W + nc] === 1) {
        const isBoundary =
          nc === 0 || nr === 0 || nc === W - 1 || nr === H - 1 ||
          dilated[nr * W + (nc - 1)] === 0 ||
          dilated[nr * W + (nc + 1)] === 0 ||
          dilated[(nr - 1) * W + nc] === 0 ||
          dilated[(nr + 1) * W + nc] === 0;
        if (isBoundary) {
          dir = d;
          cc = nc;
          cr = nr;
          found = true;
          break;
        }
      }
    }

    if (!found) break;
    if (cc === startC && cr === startR && step > 0) break;
  }

  if (contourS.length < 3) return { boundary: points, mergedIndices: [] };

  // Convert stroke boundary to (s, p) polygon for union
  const skip = Math.max(1, Math.floor(contourS.length / 120));
  let strokePoly: { s: number; p: number }[] = [];
  for (let i = 0; i < contourS.length; i += skip) {
    strokePoly.push({ s: contourS[i], p: contourP[i] });
  }

  // Union with each existing polygon — track which ones actually merged
  const mergedIndices: number[] = [];
  for (let ei = 0; ei < existingMapped.length; ei++) {
    const merged = polygonUnion2D(strokePoly, existingMapped[ei]);
    if (merged) {
      strokePoly = merged;
      mergedIndices.push(ei);
    }
  }

  // Convert final (s, p) polygon back to (station, u, v)
  // Use per-vertex station to look up the correct profile for accurate mapping
  const result: TunnelPoint3D[] = [];
  for (const pt of strokePoly) {
    const ptLookup = getLookup(pt.s);
    const uv = ptLookup.pToUV(pt.p);
    result.push({ station: pt.s, u: uv.u, v: uv.v });
  }

  return { boundary: result, mergedIndices };
}

/**
 * Create a ShaderMaterial that renders a hatched pattern.
 */
function createHatchMaterial(
  color: string,
  hatchStyle: "diagonal" | "cross" | "dots",
  fillOpacity: number,
  isSelected: boolean,
): ShaderMaterial {
  const c = new Color(isSelected ? "#ffcc00" : color);
  // hatchType: 0=diagonal, 1=cross, 2=dots
  const hatchType = hatchStyle === "diagonal" ? 0 : hatchStyle === "cross" ? 1 : 2;

  return new ShaderMaterial({
    transparent: true,
    side: DoubleSide,
    depthWrite: false,
    uniforms: {
      uColor: { value: c },
      uOpacity: { value: fillOpacity },
      uHatchType: { value: hatchType },
      uScale: { value: 3.0 }, // hatch density (world-space frequency)
    },
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform int uHatchType;
      uniform float uScale;
      varying vec3 vWorldPos;

      void main() {
        // Use world XZ for pattern (tunnel roughly along one axis)
        vec2 p = vWorldPos.xz * uScale;

        float pattern = 0.0;

        if (uHatchType == 0) {
          // Diagonal lines
          float d = abs(fract((p.x + p.y) * 0.5) - 0.5);
          pattern = smoothstep(0.05, 0.12, d);
          pattern = 1.0 - pattern; // lines are where pattern is high
        } else if (uHatchType == 1) {
          // Cross-hatch
          float d1 = abs(fract((p.x + p.y) * 0.5) - 0.5);
          float d2 = abs(fract((p.x - p.y) * 0.5) - 0.5);
          float line1 = 1.0 - smoothstep(0.05, 0.12, d1);
          float line2 = 1.0 - smoothstep(0.05, 0.12, d2);
          pattern = max(line1, line2);
        } else {
          // Dots
          vec2 cell = fract(p) - 0.5;
          float d = length(cell);
          pattern = 1.0 - smoothstep(0.12, 0.18, d);
        }

        // Combine: solid fill + hatch pattern on top
        float alpha = uOpacity * 0.4 + pattern * uOpacity * 0.8;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });
}

export interface TunnelDraw3DHandle {
  undo: () => void;
  canUndo: () => boolean;
}

/**
 * Component that provides:
 * 1. An invisible raycastable tunnel surface mesh
 * 2. Drawing interaction (pointer down/move/up -> accumulate points)
 * 3. Polygon drawing (click to place vertices, double-click to close)
 * 4. Rendering of saved + in-progress 3D lines/polygons with selection/deletion
 */
export const TunnelDraw3D = forwardRef<TunnelDraw3DHandle, TunnelDraw3DProps>(function TunnelDraw3D({ data, profileAssignments, drawMode, drawToolType = "discontinuity", brushScale, onBrushScaleChange }, ref) {
  const project = useProject();
  const meshRef = useRef<Mesh>(null);
  const [lines, setLines] = useState<Drawing3DLine[]>(() => loadDrawings3D(project.id));
  const [polygons, setPolygons] = useState<Drawing3DPolygon[]>(() => loadPolygons3D(project.id));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const currentLineRef = useRef<TunnelPoint3D[]>([]);
  const [currentWorldPts, setCurrentWorldPts] = useState<Vector3[]>([]);
  const isDrawing = useRef(false);

  // Polygon drawing state
  const [polyVertices, setPolyVertices] = useState<TunnelPoint3D[]>([]);
  const [polyWorldVerts, setPolyWorldVerts] = useState<Vector3[]>([]);

  // Brush cursor position (tracks pointer on tunnel surface)
  // Use ref for raw position, throttle state updates to avoid flicker
  const [cursorPos, setCursorPos] = useState<Vector3 | null>(null);
  const [cursorNormal, setCursorNormal] = useState<Vector3 | null>(null);
  const cursorThrottle = useRef(0);

  const isPolygonTool = DRAW_TOOL_CONFIG[drawToolType]?.isPolygon ?? false;

  // Brush scale for polygon tools (pinch to resize)
  const BRUSH_MULTIPLIER = 12; // base multiplier for polygon brush
  const effectiveBrushMultiplier = BRUSH_MULTIPLIER * brushScale;

  // Pinch-to-resize: listen for wheel (desktop) and touch pinch (mobile)
  const { gl } = useThree();
  const pinchStartDist = useRef<number | null>(null);
  const pinchStartScale = useRef(1.0);
  const isPinching = useRef(false);

  useEffect(() => {
    if (!drawMode || !isPolygonTool) return;
    const canvas = gl.domElement;

    // Desktop: scroll wheel adjusts brush size
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      onBrushScaleChange(Math.max(0.3, Math.min(3.0, brushScale * delta)));
    };

    // Mobile: intercept ALL touch events with 2+ fingers to prevent
    // R3F pointer events and browser zoom from firing during pinch.
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        isPinching.current = true;
        setCursorPos(null);
        // Cancel any in-progress drawing
        if (isDrawing.current) {
          isDrawing.current = false;
          currentLineRef.current = [];
          setCurrentWorldPts([]);
          setPolyVertices([]);
          setPolyWorldVerts([]);
        }
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDist.current = Math.sqrt(dx * dx + dy * dy);
        pinchStartScale.current = brushScale;
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2 && pinchStartDist.current !== null) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const ratio = dist / pinchStartDist.current;
        onBrushScaleChange(Math.max(0.3, Math.min(3.0, pinchStartScale.current * ratio)));
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2 && isPinching.current) {
        pinchStartDist.current = null;
        e.preventDefault();
        e.stopImmediatePropagation();
        // Delay before allowing drawing again to prevent accidental stroke on lift
        setTimeout(() => { isPinching.current = false; }, 300);
      }
    };

    // Use capture phase to intercept before R3F's pointer event system
    canvas.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false, capture: true });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false, capture: true });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: false, capture: true });

    return () => {
      canvas.removeEventListener("wheel", handleWheel, { capture: true });
      canvas.removeEventListener("touchstart", handleTouchStart, { capture: true });
      canvas.removeEventListener("touchmove", handleTouchMove, { capture: true });
      canvas.removeEventListener("touchend", handleTouchEnd, { capture: true });
      isPinching.current = false;
    };
  }, [drawMode, isPolygonTool, gl, brushScale, onBrushScaleChange]);

  // Pre-compute frames
  const { frames, curvePoints } = useMemo(() => {
    const f = data.curve.computeFrenetFrames(FRAME_SAMPLES, false);
    const pts = data.curve.getSpacedPoints(FRAME_SAMPLES);
    return { frames: f, curvePoints: pts };
  }, [data]);

  // Build surface mesh geometry
  const surfaceGeo = useMemo(
    () => buildTunnelSurfaceMesh(data, profileAssignments, frames, curvePoints),
    [data, profileAssignments, frames, curvePoints],
  );

  // Build world points + geometries for saved lines
  const lineData = useMemo(() => {
    return lines.map((line) => {
      const worldPts = line.points.map((pt) =>
        tunnelPoint3DToWorld(pt, data, frames, curvePoints, FRAME_SAMPLES),
      );

      // Tube geometry for visible rendering (radius based on tool)
      const radius = line.tubeRadius ?? 0.04;
      let visibleTubeGeo: TubeGeometry | null = null;
      let clickTubeGeo: TubeGeometry | null = null;
      if (worldPts.length >= 2) {
        const curve = new CatmullRomCurve3(worldPts, false, "catmullrom", 0.5);
        const segments = Math.max(worldPts.length * 2, 16);
        visibleTubeGeo = new TubeGeometry(curve, segments, radius, 8, false);
        clickTubeGeo = new TubeGeometry(curve, Math.max(worldPts.length, 8), Math.max(0.5, radius + 0.3), 6, false);
      }

      // Midpoint for delete button placement
      const mid = worldPts[Math.floor(worldPts.length / 2)] ?? worldPts[0];

      return { line, visibleTubeGeo, clickTubeGeo, midpoint: mid, color: line.color };
    });
  }, [lines, data, frames, curvePoints]);

  // Build polygon geometries
  const polygonData = useMemo(() => {
    return polygons.map((poly) => {
      const geo = buildPolygonGeometry(poly.vertices, data, frames, curvePoints, profileAssignments);

      // Outline: convert vertices to world, create tube for the border
      const worldVerts = poly.vertices.map((v) =>
        tunnelPoint3DToWorld(v, data, frames, curvePoints, FRAME_SAMPLES),
      );
      // Close the loop
      const closedVerts = [...worldVerts, worldVerts[0]];
      let outlineGeo: TubeGeometry | null = null;
      if (closedVerts.length >= 2) {
        const curve = new CatmullRomCurve3(closedVerts, false, "catmullrom", 0.5);
        outlineGeo = new TubeGeometry(curve, Math.max(closedVerts.length * 4, 16), 0.06, 6, false);
      }

      const centroid = new Vector3();
      for (const v of worldVerts) centroid.add(v);
      centroid.divideScalar(worldVerts.length || 1);

      return { poly, geo, outlineGeo, centroid };
    });
  }, [polygons, data, frames, curvePoints]);

  // In-progress polygon preview not needed — polygon is computed on pointer-up.
  // During drawing, the fat tube preview shows the painted area.

  // Current in-progress line/paint stroke geometry (tube)
  const currentTubeGeo = useMemo(() => {
    if (currentWorldPts.length < 2) return null;
    const curve = new CatmullRomCurve3(currentWorldPts, false, "catmullrom", 0.5);
    const cfg = DRAW_TOOL_CONFIG[drawToolType];
    // For polygon tools, show a fat brush preview while painting
    const radius = cfg.isPolygon ? cfg.tubeRadius * effectiveBrushMultiplier : cfg.tubeRadius;
    return new TubeGeometry(curve, Math.max(currentWorldPts.length * 2, 16), radius, 8, false);
  }, [currentWorldPts, drawToolType, effectiveBrushMultiplier]);

  // Hatch materials for polygon tools (memoized)
  const hatchMaterials = useMemo(() => {
    const mats: Record<string, ShaderMaterial> = {};
    for (const poly of polygons) {
      const isSelected = poly.id === selectedId;
      mats[poly.id] = createHatchMaterial(poly.color, poly.hatchStyle, poly.fillOpacity, isSelected);
    }
    return mats;
  }, [polygons, selectedId]);

  const currentPolyMaterial = useMemo(() => {
    if (!isPolygonTool) return null;
    const cfg = DRAW_TOOL_CONFIG[drawToolType];
    return createHatchMaterial(cfg.color, cfg.hatchStyle ?? "diagonal", cfg.fillOpacity ?? 0.25, false);
  }, [drawToolType, isPolygonTool]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    // Check lines
    const updatedLines = lines.filter((l) => l.id !== selectedId);
    if (updatedLines.length !== lines.length) {
      setLines(updatedLines);
      saveDrawings3D(project.id, updatedLines);
      setSelectedId(null);
      return;
    }
    // Check polygons
    const updatedPolys = polygons.filter((p) => p.id !== selectedId);
    if (updatedPolys.length !== polygons.length) {
      setPolygons(updatedPolys);
      savePolygons3D(project.id, updatedPolys);
      setSelectedId(null);
    }
  }, [selectedId, lines, polygons]);

  const undo = useCallback(() => {
    // Undo last item (line or polygon, whichever was created last)
    const lastLine = lines.length > 0 ? lines[lines.length - 1] : null;
    const lastPoly = polygons.length > 0 ? polygons[polygons.length - 1] : null;

    if (!lastLine && !lastPoly) return;

    const lineTime = lastLine?.createdAt ?? 0;
    const polyTime = lastPoly?.createdAt ?? 0;

    if (lineTime >= polyTime && lastLine) {
      const updated = lines.slice(0, -1);
      setLines(updated);
      saveDrawings3D(project.id, updated);
    } else if (lastPoly) {
      const updated = polygons.slice(0, -1);
      setPolygons(updated);
      savePolygons3D(project.id, updated);
    }
    setSelectedId(null);
  }, [lines, polygons]);

  useImperativeHandle(ref, () => ({
    undo,
    canUndo: () => lines.length > 0 || polygons.length > 0,
  }), [undo, lines, polygons]);

  // --- Pointer event handlers ---
  // Polygon tools use the same freehand drag as lines, but on pointer-up
  // the stroke is auto-closed into a filled polygon.

  const handlePointerDown = useCallback(
    (event: any) => {
      if (!drawMode || isPinching.current) return;
      const nativeEvent = event.nativeEvent || event;
      const hitPoint = event.point;
      if (!hitPoint) return;

      // Both line and polygon tools start the same way: begin freehand stroke
      isDrawing.current = true;
      const tunnelPt = worldToTunnelPoint3D(
        hitPoint, data, frames, curvePoints, FRAME_SAMPLES,
      );
      currentLineRef.current = [tunnelPt];
      setCurrentWorldPts([hitPoint.clone()]);

      if (isPolygonTool) {
        setPolyVertices([tunnelPt]);
        setPolyWorldVerts([hitPoint.clone()]);
      }

      (nativeEvent.target as HTMLElement)?.setPointerCapture?.(nativeEvent.pointerId);
      event.stopPropagation();
    },
    [drawMode, isPolygonTool, data, frames, curvePoints],
  );

  const handlePointerMove = useCallback(
    (event: any) => {
      const hitPoint = event.point;
      const faceNormal = event.face?.normal;

      // Update cursor position for brush indicator (throttled to reduce flicker)
      if (drawMode && hitPoint && !isPinching.current && !isDrawing.current) {
        const now = Date.now();
        if (now - cursorThrottle.current > 50) {
          cursorThrottle.current = now;
          setCursorPos(hitPoint.clone());
          if (faceNormal) setCursorNormal(faceNormal.clone());
        }
      }

      if (!isDrawing.current || !drawMode || isPinching.current) return;
      if (!hitPoint) return;

      const tunnelPt = worldToTunnelPoint3D(
        hitPoint, data, frames, curvePoints, FRAME_SAMPLES,
      );
      currentLineRef.current.push(tunnelPt);
      setCurrentWorldPts((prev) => [...prev, hitPoint.clone()]);

      if (isPolygonTool) {
        setPolyVertices((prev) => [...prev, tunnelPt]);
        setPolyWorldVerts((prev) => [...prev, hitPoint.clone()]);
      }

      event.stopPropagation();
    },
    [drawMode, isPolygonTool, data, frames, curvePoints],
  );

  const handlePointerUp = useCallback(
    (event: any) => {
      if (!isDrawing.current) return;
      isDrawing.current = false;

      const pts = currentLineRef.current;

      if (isPolygonTool) {
        // Extract boundary polygon from the painted area, merging with overlapping same-type polygons
        if (pts.length >= 3) {
          const cfg = DRAW_TOOL_CONFIG[drawToolType];
          const brushRadius = cfg.tubeRadius * effectiveBrushMultiplier;

          // Find existing polygons of the same tool type that might overlap with the new stroke
          // We use a rough bounding-box check on station range to find candidates
          const strokeMinS = Math.min(...pts.map((p) => p.station)) - brushRadius * 2;
          const strokeMaxS = Math.max(...pts.map((p) => p.station)) + brushRadius * 2;
          const sameTypePolys = polygons.filter(
            (p) => p.toolType === drawToolType &&
              Math.max(...p.vertices.map((v) => v.station)) >= strokeMinS &&
              Math.min(...p.vertices.map((v) => v.station)) <= strokeMaxS,
          );

          const { boundary, mergedIndices } = extractBoundaryFromPaintStroke(
            pts, brushRadius, profileAssignments,
            sameTypePolys.length > 0 ? sameTypePolys : undefined,
          );
          if (boundary.length >= 3) {
            // Only remove polygons that were actually merged (overlapping)
            const mergedIds = new Set(mergedIndices.map((i) => sameTypePolys[i].id));
            const remaining = polygons.filter((p) => !mergedIds.has(p.id));
            // Preserve the oldest merged polygon's id and createdAt
            const mergedPolys = mergedIndices.map((i) => sameTypePolys[i]);
            const oldest = mergedPolys.length > 0
              ? mergedPolys.reduce((a, b) => a.createdAt < b.createdAt ? a : b)
              : null;
            const newPoly: Drawing3DPolygon = {
              id: oldest?.id ?? crypto.randomUUID(),
              vertices: boundary,
              color: cfg.color,
              toolType: drawToolType,
              hatchStyle: cfg.hatchStyle ?? "diagonal",
              fillOpacity: cfg.fillOpacity ?? 0.25,
              createdAt: oldest?.createdAt ?? Date.now(),
              updatedAt: Date.now(),
            };
            const updated = [...remaining, newPoly];
            setPolygons(updated);
            savePolygons3D(project.id, updated);
          }
        }
        setPolyVertices([]);
        setPolyWorldVerts([]);
      } else {
        // Line mode
        if (pts.length >= 2) {
          const cfg = DRAW_TOOL_CONFIG[drawToolType];
          const newLine: Drawing3DLine = {
            id: crypto.randomUUID(),
            points: pts,
            color: cfg.color,
            lineWidth: cfg.lineWidth,
            tubeRadius: cfg.tubeRadius,
            toolType: drawToolType,
            createdAt: Date.now(),
          };
          const updated = [...lines, newLine];
          setLines(updated);
          saveDrawings3D(project.id, updated);
        }
      }

      currentLineRef.current = [];
      setCurrentWorldPts([]);
      event.stopPropagation();
    },
    [lines, polygons, drawToolType, isPolygonTool, effectiveBrushMultiplier, profileAssignments],
  );

  // Click on background surface deselects
  const handleSurfaceClick = useCallback(
    (event: any) => {
      if (drawMode) return;
      setSelectedId(null);
    },
    [drawMode],
  );

  // Find selected item midpoint for delete button
  const selectedMidpoint = useMemo(() => {
    if (!selectedId) return null;
    // Check lines
    const ld = lineData.find((d) => d.line.id === selectedId);
    if (ld) return ld.midpoint;
    // Check polygons
    const pd = polygonData.find((d) => d.poly.id === selectedId);
    if (pd) return pd.centroid;
    return null;
  }, [selectedId, lineData, polygonData]);

  return (
    <group>
      {/* Raycastable tunnel surface */}
      <mesh
        ref={meshRef}
        geometry={surfaceGeo}
        onPointerDown={drawMode ? handlePointerDown : undefined}
        onPointerMove={drawMode ? handlePointerMove : undefined}
        onPointerUp={drawMode ? handlePointerUp : undefined}
        onPointerLeave={drawMode ? () => setCursorPos(null) : undefined}
        onClick={!drawMode ? handleSurfaceClick : undefined}
      >
        <meshBasicMaterial
          side={DoubleSide}
          color={drawMode ? "#4488ff" : "#000000"}
          transparent
          opacity={drawMode ? 0.08 : 0}
          depthWrite={false}
        />
      </mesh>

      {/* Saved lines */}
      {lineData.map(({ line, visibleTubeGeo, clickTubeGeo, midpoint, color }) => {
        const isSelected = line.id === selectedId;
        return (
          <group key={line.id}>
            {/* Visible tube */}
            {visibleTubeGeo && (
              <mesh geometry={visibleTubeGeo}>
                <meshBasicMaterial
                  color={isSelected ? "#ffcc00" : color}
                  side={DoubleSide}
                />
              </mesh>
            )}

            {/* Invisible fat tube for click detection — only when not drawing */}
            {!drawMode && clickTubeGeo && (
              <mesh
                geometry={clickTubeGeo}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedId(isSelected ? null : line.id);
                }}
              >
                <meshBasicMaterial transparent opacity={0} side={DoubleSide} depthWrite={false} />
              </mesh>
            )}
          </group>
        );
      })}

      {/* Saved polygons */}
      {polygonData.map(({ poly, geo, outlineGeo, centroid }) => {
        const isSelected = poly.id === selectedId;
        const mat = hatchMaterials[poly.id];
        return (
          <group key={poly.id}>
            {/* Hatched fill */}
            {mat && (
              <mesh geometry={geo} material={mat} />
            )}
            {/* Outline */}
            {outlineGeo && (
              <mesh geometry={outlineGeo}>
                <meshBasicMaterial
                  color={isSelected ? "#ffcc00" : poly.color}
                  side={DoubleSide}
                />
              </mesh>
            )}
            {/* Click detection on filled area + outline — only when not drawing */}
            {!drawMode && (
              <>
                <mesh
                  geometry={geo}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(isSelected ? null : poly.id);
                  }}
                >
                  <meshBasicMaterial transparent opacity={0} side={DoubleSide} depthWrite={false} />
                </mesh>
                {outlineGeo && (
                  <mesh
                    geometry={outlineGeo}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedId(isSelected ? null : poly.id);
                    }}
                  >
                    <meshBasicMaterial transparent opacity={0} side={DoubleSide} depthWrite={false} />
                  </mesh>
                )}
              </>
            )}
          </group>
        );
      })}

      {/* Current in-progress paint/line stroke */}
      {currentTubeGeo && (
        <mesh geometry={currentTubeGeo}>
          <meshBasicMaterial color={DRAW_TOOL_CONFIG[drawToolType].color} side={DoubleSide} transparent opacity={0.8} />
        </mesh>
      )}

      {/* Brush cursor — ring on tunnel surface showing brush size (desktop only, hidden during pinch/draw) */}
      {drawMode && isPolygonTool && cursorPos && !isDrawing.current && !isPinching.current && !("ontouchstart" in window) && (() => {
        const cfg = DRAW_TOOL_CONFIG[drawToolType];
        const brushR = cfg.tubeRadius * effectiveBrushMultiplier;
        return (
          <group position={[cursorPos.x, cursorPos.y, cursorPos.z]}>
            <mesh
              rotation={[-Math.PI / 2, 0, 0]}
              // Ring lies in XZ plane by default; surface normal alignment
              // would be ideal but this is good enough for a cursor indicator
            >
              <ringGeometry args={[brushR * 0.92, brushR, 48]} />
              <meshBasicMaterial
                color={cfg.color}
                side={DoubleSide}
                transparent
                opacity={0.5}
                depthWrite={false}
              />
            </mesh>
            {/* Semi-transparent fill to show area */}
            <mesh rotation={[-Math.PI / 2, 0, 0]}>
              <circleGeometry args={[brushR, 48]} />
              <meshBasicMaterial
                color={cfg.color}
                side={DoubleSide}
                transparent
                opacity={0.1}
                depthWrite={false}
              />
            </mesh>
            {/* Size label */}
            <Html center style={{ pointerEvents: "none" }}>
              <div style={{
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                fontSize: "0.65rem",
                fontWeight: 700,
                padding: "2px 6px",
                borderRadius: 8,
                whiteSpace: "nowrap",
                transform: "translateY(-24px)",
              }}>
                {(brushR * 2).toFixed(1)}m
              </div>
            </Html>
          </group>
        );
      })()}

      {/* Delete button for selected item */}
      {selectedId && !drawMode && selectedMidpoint && (
        <Html position={[selectedMidpoint.x, selectedMidpoint.y + 1.5, selectedMidpoint.z]} center>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteSelected();
            }}
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(180, 40, 40, 0.85)",
              color: "#fff",
              border: "1.5px solid rgba(255,100,100,0.5)",
              borderRadius: "8px",
              cursor: "pointer",
              padding: 0,
              backdropFilter: "blur(8px)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}
            title="Delete"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
          </button>
        </Html>
      )}

    </group>
  );
});
